import { t } from "i18next"
import {
    Avatar,
    Button,
    Chip,
    Dropdown,
    EPopupBranding,
    EPopupDisplayFrequency,
    EPopupPosition,
    EPopupTriggerType,
    fetchRoute,
    getCalendarFormat,
    Icon,
    Link,
    TDataTableColumnsData,
    TPopupModel,
    Typography
} from "@cakemail-org/ui-components-v2"
import { Stack } from "@mui/material"
import { TGetListingRowsPopup } from "./types"
import { NameTableCol } from "../../components/tables/nameTableCol"
import { ERoutes } from "../../utils/url"


export function getEmptyPopupData(): TPopupModel {
    return {
        id: "",
        account_id: 0,
        created_on: 0,
        list_id: 0,
        form_id: "",

        name: t("general-untitled"),
        description: "",
        content: {
            position: EPopupPosition.center,
            json: {}
        },
        branding: EPopupBranding.smart,
        trigger: {
            delay_seconds: null,
            start_date: null,
            end_date: null,
            scroll_percentage: null,
            selector: null,
            type: EPopupTriggerType.on_page_load
        },
        targeting: {
            browsers: null,
            countries: null,
            devices: null,
            exclude_pages: null,
            languages: null,
            pages: null,
            referrers: null,
            utm_params: null
        },
        edited_by: {
            email: "",
            id: ""
        },
        enabled: true,
        published_on: null,
        thumbnail_url: "",
        updated_on: 0,
        display_frequency: EPopupDisplayFrequency.always,
        tags: null
    }
}

export function getPositionStyle(position: EPopupPosition) {
    let style = `
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    `
    switch (position) {
        case 'classic-center':
            style = `
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            `
            break
        case 'classic-top-right':
            style = `
            top: 0;
            right: 0;
            `
            break
        case 'classic-top-left':
            style = `
            top: 0;
            left: 0;
            `
            break
        case 'classic-bottom-left':
            style = `
            bottom: 0;
            left: 0;
            `
            break
        case 'classic-bottom-right':
            style = `
            bottom: 0;
            right: 0;
            `
            break
        case 'drawer-left':
            style = `
            top: 0;
            left: 0;
            height: 100vh;
            display: flex;
            align-items: center;
            `
            break
        case 'drawer-right':
            style = `
            top: 0;
            right: 0;
            height: 100vh;
            display: flex;
            align-items: center;
            `
            break
        case 'bar-top':
            style = `
            top: 0;
            left: 0;
            width: 100%;
            height: auto;
            `
            break
        case 'bar-bottom':
            style = `
            bottom: 0;
            left: 0;
            width: 100%;
            height: auto;
            `
            break
    }

    const completeStyle = `
    <style>    
        body{
            margin: 0;
            padding: 0;
        }
        .bee-popup-container {
            position:absolute;
            ${style}
        }
    </style>
    `
    return completeStyle
}


export function getListingColsData(): () => TDataTableColumnsData[] {
    return () => [
        {
            id: "name",
            label: t("general-popup_other"),
            sortable: true
        },
        /*{
            id: "conversion",
            label: t("general-conversion"),
            sortable: true,
        },
        {
            id: "views",
            label: t("general-view_other"),
            sortable: true,
        },*/
        {
            id: "published_on",
            label: t("general-status"),
            sortable: true
        },
        {
            id: "branding",
            label: t("general-branding"),
            sortable: true
        },
        {
            id: "edited_by",
            label: t("general-edited_by")
        },
        {
            id: "created_on",
            label: t("general-created_on"),
            sortable: true
        },
        {
            id: "updated_on",
            label: t("general-updated_on"),
            sortable: true
        },
        {
            id: "list_id",
            label: t("general-list_one"),
            sortable: true
        },
        {
            id: "actions",
            label: ""
        }
    ]
}

export function getListingRows({
    popup,
    partialList,
    users,
    handleOpenPopup,
    handlePreviewPopup,
    handleRenamePopup,
    handleDuplicatePopup,
    handleDeletePopup
}: TGetListingRowsPopup) {
    const actions = <Stack direction="row" gap={3}>
        <Button
            onClick={() => { handleOpenPopup(popup) }}
            size="small"
            color="body1"
            variant="outlined"
        >{t("general-open_one")}</Button>
        <Dropdown
            size="medium"
            color="body1"
            variant="outlined"
            iconOnly
            options={[
                {
                    isAction: true,
                    text: t("general-preview"),
                    postContent: <Icon name="Preview18" />,
                    onClick: () => { handlePreviewPopup(popup) }
                },
                {
                    isAction: true,
                    text: t("general-rename"),
                    postContent: <Icon name="Edit18" />,
                    onClick: () => { handleRenamePopup(popup) }
                },
                {
                    isAction: true,
                    text: t("general-duplicate"),
                    postContent: <Icon name="Document18" />,
                    onClick: () => { handleDuplicatePopup(popup) }
                },
                {
                    isAction: true,
                    text: t("general-delete"),
                    postContent: <Icon name="Delete18" />,
                    onClick: () => { handleDeletePopup(popup) }
                }
            ]}
        />
    </Stack>

    const editedBy = users && users[popup.edited_by.id]
    const isSmartBranding = popup.branding === "smart"
    return {
        name: {
            id: popup.name || "",
            content: <NameTableCol name={popup.name || ""} desc={popup.description || ""} imgSrc={popup.thumbnail_url} />
        },
        edited_by: editedBy ? <Stack direction="row" gap={3} alignItems="center">
            <Avatar variant="square" email={editedBy.email} firstName={editedBy.first_name} lastName={editedBy.last_name} />
            <Typography variant="body1L">{editedBy.first_name} {editedBy.last_name}</Typography>
        </Stack> : "-",
        list_id: <Link href={"#" + fetchRoute({ route: ERoutes.contacts }) + "?listId=" + popup.list_id}>{partialList?.name || popup.list_id}</Link>,
        published_on: <Chip label={popup.published_on && popup.published_on > 0 ? t("general-published") : t("general-unpublished")} color={popup.published_on && popup.published_on > 0 ? "secondary" : "primary"} />,
        branding: <Chip label={t(isSmartBranding ? "general-smart" : "general-basic")} color={isSmartBranding ? "shade150" : "shade150"} />,
        created_on: <Typography variant="body1L">{popup.created_on !== 0 ? getCalendarFormat({ unixDate: popup.created_on, showTime: true }) : ""}</Typography>,
        updated_on: <Typography variant="body1L">{popup.updated_on !== 0 ? getCalendarFormat({ unixDate: popup.updated_on, showTime: true }) : ""}</Typography>,
        actions
    }
}
